/**
 * How hard, and how politely, the softphone tries to get its registration back.
 *
 * Kept out of the store because these are numbers to tune and to stub, not behaviour: the store
 * owns the loop (it is the only thing that can re-mint a token and resume a campaign), and this
 * owns how long that loop waits and how many times it bothers.
 *
 * The shape of the problem the numbers answer:
 *
 * - The common drop is a Wi-Fi hop, a VPN reconnect or a laptop waking up, and it is over in a
 *   second or two. So the first wait is short - an agent whose network blinked should be back
 *   before they notice - and the last is long enough that a slower outage is still caught.
 * - Every agent's laptop waking at 09:00 would otherwise hit the token endpoint and Telnyx in
 *   lockstep. JITTER_RATIO spreads them.
 * - Attempts are bounded because the give-up state is a real state an agent can act on (offline,
 *   campaign paused, a message saying so), and a widget that retries forever never reaches it.
 */
export const RECONNECT_POLICY_DEFAULTS = Object.freeze({
  // Five attempts over roughly half a minute: long enough for a Wi-Fi hop or a VPN reconnect,
  // short enough that an agent staring at a paused campaign is told to go live again rather than
  // left watching a spinner.
  maxAttempts: 5,

  baseDelayInMs: 1000,

  backoffFactor: 2,

  // The per-attempt cap. Without it the fifth wait would be 16s and a sixth would be half a
  // minute on its own.
  maxDelayInMs: 15000,

  // +/- 30% on each wait, so a fleet of agents that dropped together does not come back together.
  jitterRatio: 0.3,

  /**
   * How long an attempt waits for the registration after `connect()` has resolved. Connecting and
   * registering are two different things in the Telnyx SDK - `connect()` settles on the socket,
   * `telnyx.ready` on the registration - and an attempt that connected to a socket that never
   * registers has to give way to the next attempt rather than hang the loop.
   */
  registrationTimeoutInMs: 10000,

  /**
   * How long before a token's `expiresAt` the widget re-mints it. The point is that the common
   * case never becomes a reconnect at all: the token is replaced while the agent is idle, not
   * repaired after the socket it was authenticating dropped.
   */
  tokenRenewalMarginInMs: 5 * 60 * 1000,

  /**
   * How long a reconnect owed behind a live call waits for that call to end before it stops
   * waiting.
   *
   * The call it is waiting on is being reported by the socket that dropped, so a far end hanging
   * up during the outage cannot reach this tab and the wait would never end on its own. Long
   * enough that an ordinary call is never cut short by it - the media can outlive the signalling,
   * and an agent still talking must not be interrupted to fix a socket - and short enough that a
   * call nobody is on stops holding the registration hostage for the rest of the shift.
   */
  deferredReconnectTimeoutInMs: 5 * 60 * 1000
})

let policy = { ...RECONNECT_POLICY_DEFAULTS }

export function getReconnectPolicy() {
  return policy
}

/**
 * Test and e2e seam. A Playwright test that had to sit through the real backoff to see the
 * give-up state would take longer than the rest of the suite put together, so it turns the waits
 * down instead of the suite waiting for them. Reached in the browser only through the softphone
 * dev hook, which is stripped from production builds.
 */
export function configureReconnectPolicy(overrides = {}) {
  policy = { ...policy, ...overrides }

  return policy
}

export function resetReconnectPolicy() {
  policy = { ...RECONNECT_POLICY_DEFAULTS }

  return policy
}

/**
 * How long to wait before `attempt` (1-based). Exponential, capped, then jittered - in that order,
 * so the cap bounds the growth and the jitter still spreads the agents that reach it.
 *
 * Jitter is proportional rather than absolute, which keeps the waits ordered: attempt 2's shortest
 * possible wait is still longer than attempt 1's longest, so "the backoff grows" holds for every
 * roll of the dice and not just on average.
 */
export function getReconnectDelayInMs(attempt, overrides = {}) {
  const { baseDelayInMs, backoffFactor, maxDelayInMs, jitterRatio } = { ...policy, ...overrides }

  const exponential = baseDelayInMs * Math.pow(backoffFactor, Math.max(0, attempt - 1))
  const capped = Math.min(maxDelayInMs, exponential)
  const jitter = capped * jitterRatio * (Math.random() * 2 - 1)

  return Math.max(0, Math.round(capped + jitter))
}
